import { PlusOutlined } from '@ant-design/icons';
import { DndContext, DragOverlay } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useRelationEditor } from '@framework/form/form/relations/useRelationEditor.ts';
import { FormItem } from '@framework/form/item/FormItem.tsx';
import { useBasicDndDragger } from '@framework/ui/core/dnd/hooks/useBasicDndDragger.ts';
import { Alert, Input } from 'antd';
import { useSectionType } from '../../../../../sections/SectionTypeContext.tsx';
import { useWidgetCreationDefaults } from '../../../WidgetCreationDefaultsContext.tsx';
import { type ITableColumnWidget } from '../ITableInputWidget.ts';
import { createTableWidgetInitialColumn } from './createTableWidgetInitialColumn.ts';
import { SortableTableColumnItem } from './SortableTableColumnItem.tsx';
import { TableColumnView } from './TableColumnView.tsx';
import { TableColumnWidgetEditDrawer } from './TableColumnWidgetEditDrawer.tsx';

export interface ITableColumnWidgetListProps {
  name: string;
}

/**
 * Componente con D'n'D per la configurazione della lista delle opzioni di widget checkbox/radio/select
 */
export function TableColumnWidgetList(props: ITableColumnWidgetListProps) {
  const { name } = props;

  const editor = useRelationEditor<ITableColumnWidget>(name);

  const { sectionType } = useSectionType();
  const { defaultRequired } = useWidgetCreationDefaults();
  const { sensors, handleDragStart, handleDragEnd, itemIds, active } =
    useBasicDndDragger(editor);

  return (
    <div>
      {editor.items.length < 1 && (
        <Alert
          type="warning"
          showIcon
          message="Aggiungere almeno una Colonna"
        />
      )}

      <FormItem name={name} label={null}>
        <DndContext
          sensors={sensors}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToVerticalAxis]}
        >
          <SortableContext
            items={itemIds}
            strategy={verticalListSortingStrategy}
          >
            {editor.items.map((item, index) => (
              <SortableTableColumnItem
                item={item}
                uid={item.__uid!}
                key={item.__uid}
                active={active}
                onEdit={e => {
                  editor.handle.open(item);
                }}
                onRemove={e => {
                  editor.remove(item);
                }}
                onAdd={e => {
                  const widget = createTableWidgetInitialColumn(
                    item.type,
                    {
                      label: 'Nuova colonna',
                      code: `colonna_${editor.items.length}`,
                    },
                    sectionType,
                    defaultRequired,
                  );

                  if (!widget) return;

                  editor.handle.open(widget);
                }}
              />
            ))}
            <DragOverlay>
              {active && <TableColumnView item={active} />}
            </DragOverlay>
          </SortableContext>
        </DndContext>

        {/* Pulsanti da aggiunta */}
        <Input.Group compact>
          <TableColumnWidgetEditDrawer
            editor={editor}
            buttonProps={{
              type: 'dashed',
              size: 'large',
              icon: <PlusOutlined aria-hidden="true" />,
              style: { width: '40%' },
              children: 'Aggiungi Campo',
            }}
          />
        </Input.Group>
      </FormItem>
    </div>
  );
}
